import { useEffect, useState } from "react";

const RTF = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const DTF = new Intl.DateTimeFormat("en", {
  dateStyle: "medium",
  timeStyle: "short",
});

interface Unit {
  name: Intl.RelativeTimeFormatUnit;
  ms: number;
}

const UNITS: Unit[] = [
  { name: "year", ms: 1000 * 60 * 60 * 24 * 365 },
  { name: "month", ms: 1000 * 60 * 60 * 24 * 30 },
  { name: "week", ms: 1000 * 60 * 60 * 24 * 7 },
  { name: "day", ms: 1000 * 60 * 60 * 24 },
  { name: "hour", ms: 1000 * 60 * 60 },
  { name: "minute", ms: 1000 * 60 },
  { name: "second", ms: 1000 },
];

function formatRelative(epochMs: number): string {
  const diff = epochMs - Date.now();
  const absDiff = Math.abs(diff);
  for (const unit of UNITS) {
    if (absDiff >= unit.ms || unit.name === "second") {
      const value = Math.round(diff / unit.ms);
      return RTF.format(value, unit.name);
    }
  }
  return RTF.format(0, "second");
}

/**
 * Returns a human-friendly "X ago" string for the given epoch (in ms),
 * refreshing on the supplied interval (default 5 s).
 */
export function useRelativeTime(
  epochMs: number | null,
  intervalMs = 5000
): { relative: string; absolute: string } {
  const [, setTick] = useState(0);
  const hasValidEpoch = epochMs != null && Number.isFinite(epochMs);

  useEffect(() => {
    if (!hasValidEpoch) return;
    const id = window.setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [hasValidEpoch, intervalMs]);

  if (!hasValidEpoch) {
    return { relative: "", absolute: "" };
  }

  return {
    relative: formatRelative(epochMs),
    absolute: DTF.format(new Date(epochMs)),
  };
}
